/**
 * De beheerkant van de subsidiemodule: regelingen, regels en bedragen
 * aanmaken, wijzigen en verwijderen zonder migratie.
 *
 * Wat dit scherm bewust wél laat zien:
 *  - hoe oud de bron van elke regeling is (badge, en verouderde regelingen
 *    krijgen een gekleurde rand);
 *  - welke regels op een veld staan dat de evaluatiekern niet kan lezen;
 *  - hoeveel regels en bedragen een regeling heeft, want een regeling met nul
 *    regels wordt door de kern als "ongetoetst" behandeld
 *    (`subsidie-evaluatie-core.ts:839-842`) en levert dus een bedrag zonder
 *    toets.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CalendarCheck, ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import type { SubsidieRegeling, SubsidieRegel, SubsidieBedrag } from '@/hooks/useSubsidieRegelingen';
import {
  leesBijgewerktDoor,
  leesBronGecontroleerdOp,
  leesUpdatedAt,
  useSchrijversNamen,
  useSubsidieBeheer,
  useSubsidieDossiers,
  useSubsidieSchemaStand,
} from '@/hooks/useSubsidieBeheer';
import { REDEN, bannerVoorRechten, schrijfrechtenVoorRol } from '@/hooks/subsidie-schrijfrecht';
import {
  berekenVoorvertoning, type Catalogus, type Voorstel, type Voorvertoning,
} from '@/hooks/subsidie-voorvertoning';
import { useAuth } from '@/hooks/useAuth';
import { BronControleBadge } from './BronControleBadge';
import { RegelingDialog } from './RegelingDialog';
import { RegelDialog } from './RegelDialog';
import { BedragDialog } from './BedragDialog';
import { VoorvertoningDialog } from './VoorvertoningDialog';
import { bronVersheid, vandaagAlsDatum } from './bron-versheid';
import { OPERATOR_LABELS, isBekendVeld, onbekendVeldUitleg, operatorPastBijVeld, vindIntakeVeld, type RegelOperator } from './intake-velden';

function waardeAlsTekst(waarde: unknown): string {
  if (Array.isArray(waarde)) return waarde.map(d => String(d)).join(', ');
  if (waarde === null || waarde === undefined) return '(leeg)';
  if (typeof waarde === 'object') return JSON.stringify(waarde);
  return String(waarde);
}

/**
 * Eén schrijfactie die op zijn bevestiging wacht: de gemeten voorvertoning, de
 * zin die zegt wát er wordt opgeslagen, en de functie die het daadwerkelijk
 * doet. De sleutel gaat mee naar de mutatie; die schrijft niet zonder.
 */
interface TeBevestigen {
  voorvertoning: Voorvertoning;
  titel: string;
  schrijf: (bevestiging: string | null) => void;
}

/**
 * Een uitgeschakelde knop vertelt niets. Deze omhulling geeft hem een reden
 * mee, en doet dat op het OMLIGGENDE element: shadcn zet
 * `disabled:pointer-events-none` op de knop zelf, dus een `title` dáárop wordt
 * nooit getoond.
 *
 * Verbergen is bewust geen optie. Een knop die verdwijnt leert niemand dat de
 * handeling bestaat en aan iemand anders toekomt; een knop die grijs staat met
 * een reden wel.
 */
function MetReden({ mag, reden, children }: { mag: boolean; reden: string; children: ReactNode }) {
  if (mag) return <>{children}</>;
  return <span title={reden} className="inline-flex cursor-not-allowed">{children}</span>;
}

export function SubsidieBeheerPaneel({
  jaar,
  regelingen,
  regels,
  bedragen,
  isLoading,
}: {
  jaar: number;
  regelingen: SubsidieRegeling[];
  regels: SubsidieRegel[];
  bedragen: SubsidieBedrag[];
  isLoading: boolean;
}) {
  const beheer = useSubsidieBeheer();
  const { userRole } = useAuth();
  /**
   * Wat deze gebruiker écht mag, in dezelfde indeling als de policies en de
   * kolomsloten in de database (migraties 070 en 083). Eén bron, zodat een knop
   * nooit iets aanbiedt dat bij opslaan stil nul rijen raakt — en nooit iets
   * dichtzet dat wél mag.
   */
  /**
   * Wat de database vandaag werkelijk toestaat, gemeten in plaats van
   * aangenomen: zolang 083 niet is toegepast geldt de smalle whitelist van 070
   * en bestaan de policies op sub_regels en sub_bedragen niet. Bij twijfel de
   * smalle stand — dan belooft het scherm te weinig in plaats van te veel.
   */
  const schema = useSubsidieSchemaStand();
  const stand = schema.data ?? { migratie083: false };
  const rechten = useMemo(() => schrijfrechtenVoorRol(userRole, stand), [userRole, stand.migratie083]);
  const banner = useMemo(() => bannerVoorRechten(rechten, stand), [rechten, stand.migratie083]);
  const schrijvers = useSchrijversNamen(regelingen.map(r => leesBijgewerktDoor(r)));
  /**
   * De dossiers waartegen de voorvertoning rekent. Eén query voor het hele
   * paneel; met de gemeten 5 intakes en 24 matches kost een voorvertoning
   * daarna 0,12 ms en hoeft niemand op het netwerk te wachten.
   */
  const dossiers = useSubsidieDossiers();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [regelingDialoog, setRegelingDialoog] = useState<{ open: boolean; regeling: SubsidieRegeling | null }>({ open: false, regeling: null });
  const [regelDialoog, setRegelDialoog] = useState<{ open: boolean; regelingId: string; regel: SubsidieRegel | null }>({ open: false, regelingId: '', regel: null });
  const [bedragDialoog, setBedragDialoog] = useState<{ open: boolean; regelingId: string; bedrag: SubsidieBedrag | null }>({ open: false, regelingId: '', bedrag: null });
  const [teBevestigen, setTeBevestigen] = useState<TeBevestigen | null>(null);

  const vandaag = useMemo(() => new Date(), []);
  const catalogus = useMemo<Catalogus>(() => ({ regelingen, regels, bedragen }), [regelingen, regels, bedragen]);

  /**
   * De enige weg naar een schrijfactie.
   *
   * Reken eerst uit wat dit voorstel met de lopende dossiers doet. Verschuift
   * er niets en is er niets te benoemen, dan gaat het meteen door — teksten,
   * toelichtingen en sorteervolgorde slaan gewoon op. Verschuift er wél iets,
   * dan komt de voorvertoning in beeld en wordt er pas geschreven als de
   * gebruiker daar op de knop mét het bedrag drukt.
   *
   * De voorvertoning gaat óók mee naar de mutatie. Die roept
   * `bewaakBevestiging` aan vóór hij Supabase aanraakt, zodat een tweede klik,
   * een herrender of een latere aanroeper die deze functie omzeilt alsnog
   * strandt in plaats van stil te schrijven.
   */
  function metVoorvertoning(
    voorstel: Voorstel,
    titel: string,
    gezienOp: string | null,
    schrijf: (arg: {
      voorstel: Voorstel;
      voorvertoning: Voorvertoning;
      bevestiging: string | null;
      gezienOp: string | null;
    }) => void,
  ) {
    // ÉLKE intake, niet alleen die met een sub_matches-rij — zie
    // `useSubsidieDossiers` voor waarom dat een gat was.
    const alle = dossiers.data?.dossiers ?? [];
    const voorvertoning = berekenVoorvertoning(catalogus, voorstel, alle);
    if (voorvertoning.bevestigingSleutel === null) {
      schrijf({ voorstel, voorvertoning, bevestiging: null, gezienOp });
      return;
    }
    setTeBevestigen({
      voorvertoning,
      titel,
      schrijf: bevestiging => schrijf({ voorstel, voorvertoning, bevestiging, gezienOp }),
    });
  }

  /**
   * Een knop die schrijft staat pas aan als de voorvertoning ook echt gerekend
   * kán worden. Laden de dossiers nog, of faalde die query, dan is er niets om
   * tegen te vergelijken — en een schrijfactie zonder vergelijking is precies
   * wat dit ticket weghaalt.
   */
  const mag = (recht: boolean) =>
    recht && dossiers.isSuccess && schema.isSuccess && !beheer.isBezig;

  if (isLoading) return <p className="text-muted-foreground">Laden...</p>;

  return (
    <div className="space-y-4">
      {/*
        Zonder de dossiers is er geen voorvertoning, en dan hoort er niet
        geschreven te worden. Blind doorgaan zou het besluit van Stan precies
        omdraaien: dan verschuift er stil weer een bedrag.
      */}
      {dossiers.isError && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <strong className="font-medium">De lopende dossiers zijn niet te lezen.</strong> Zonder die
            dossiers kan het scherm niet laten zien wat een wijziging met een offertebedrag doet, en dan
            wordt er niets opgeslagen. Ververs de pagina; blijft dit staan, meld het dan.
          </span>
        </div>
      )}

      {banner.toon && (
        <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <strong className="font-medium">{banner.kop}</strong> {banner.uitleg}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {regelingen.length} regelingen in {jaar}. Wijzigingen gaan direct naar de database en werken meteen
          door in elke intake-uitkomst.
        </p>
        <MetReden mag={rechten.regelingAanmaken} reden={REDEN.regelingAanmaken}>
          <Button
            size="sm"
            disabled={!mag(rechten.regelingAanmaken)}
            onClick={() => setRegelingDialoog({ open: true, regeling: null })}
          >
            <Plus className="h-4 w-4 mr-1" /> Nieuwe regeling
          </Button>
        </MetReden>
      </div>

      {regelingen.map(regeling => {
        const datum = leesBronGecontroleerdOp(regeling);
        const versheid = bronVersheid(datum, vandaag);
        // `sort_order` is nullable in de database (default 0); zonder de
        // terugval levert een null-waarde NaN op en verspringt de volgorde.
        const eigenRegels = regels
          .filter(r => r.regeling_id === regeling.id)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const eigenBedragen = bedragen.filter(b => b.regeling_id === regeling.id);
        const uitgeklapt = open[regeling.id] ?? false;
        const problematischeRegels = eigenRegels.filter(
          r => !isBekendVeld(r.veld) || !operatorPastBijVeld(r.veld, r.operator),
        );

        return (
          <Card
            key={regeling.id}
            /* Een verouderde of nooit gecontroleerde bron ziet er anders uit —
               dat is de hele reden dat de kolom bestaat. */
            className={versheid.status === 'vers' ? '' : 'border-amber-400 border-2'}
          >
            <CardContent className="py-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      className="flex items-center gap-1 font-semibold text-lg"
                      onClick={() => setOpen(o => ({ ...o, [regeling.id]: !uitgeklapt }))}
                    >
                      {uitgeklapt ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {regeling.korte_naam}
                    </button>
                    <Badge variant="outline">{regeling.code}</Badge>
                    <Badge variant="outline">{regeling.status}</Badge>
                    <BronControleBadge datum={datum} vandaag={vandaag} />
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{regeling.naam}</p>
                  <p className="text-xs text-muted-foreground">
                    {eigenRegels.length} {eigenRegels.length === 1 ? 'regel' : 'regels'} — {eigenBedragen.length}{' '}
                    {eigenBedragen.length === 1 ? 'bedrag' : 'bedragen'}
                    {/* Wie deze regeling het laatst bijwerkte. Alleen tonen als
                        `bijgewerkt_door` gevuld is: leeg betekent "niet via de
                        app geschreven" (SQL-editor, migratie, pg_cron), en dat
                        is iets anders dan "door niemand". */}
                    {(() => {
                      const door = leesBijgewerktDoor(regeling);
                      if (!door) return null;
                      const naam = schrijvers.data?.[door] ?? `account ${door.slice(0, 8)}`;
                      return <> — laatst bijgewerkt door {naam}</>;
                    })()}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <MetReden mag={rechten.regelingBijwerken} reden={REDEN.geenSchrijfrecht}>
                    <Button
                      variant="outline"
                      size="sm"
                      title="Ik heb de RVO-pagina vandaag nagelopen en er hoefde niets te veranderen"
                      onClick={() => beheer.markeerGecontroleerd.mutate({ id: regeling.id, datum: vandaagAlsDatum() })}
                      disabled={!mag(rechten.regelingBijwerken)}
                    >
                      <CalendarCheck className="h-4 w-4 mr-1" /> Gecontroleerd
                    </Button>
                  </MetReden>
                  <MetReden mag={rechten.regelingBijwerken} reden={REDEN.geenSchrijfrecht}>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Regeling wijzigen"
                      disabled={!mag(rechten.regelingBijwerken)}
                      onClick={() => setRegelingDialoog({ open: true, regeling })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </MetReden>
                  <MetReden mag={rechten.regelingVerwijderen} reden={REDEN.verwijderen}>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Regeling verwijderen"
                      disabled={!mag(rechten.regelingVerwijderen)}
                      onClick={() => metVoorvertoning(
                        { soort: 'regeling-verwijderen', id: regeling.id, regelingId: regeling.id },
                        `Verwijderen: regeling ${regeling.korte_naam} (${regeling.code}), inclusief ${eigenRegels.length} regels en ${eigenBedragen.length} bedragen.`,
                        null,
                        arg => beheer.verwijderRegeling.mutate(
                          { id: regeling.id, jaar, ...arg },
                          { onSettled: () => setTeBevestigen(null) },
                        ),
                      )}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </MetReden>
                </div>
              </div>

              {versheid.status !== 'vers' && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{versheid.tekst}</span>
                </div>
              )}

              {eigenRegels.length === 0 && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Geen enkele toetsingsregel. De rekenkern behandelt deze regeling als ongetoetst: er komt wel
                    een bedrag uit, maar er is niets gecontroleerd.
                  </span>
                </div>
              )}

              {problematischeRegels.length > 0 && (
                <div className="flex items-start gap-2 rounded-md bg-red-50 p-2 text-sm text-red-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {problematischeRegels.length} {problematischeRegels.length === 1 ? 'regel doet' : 'regels doen'}{' '}
                    niets of blokkeert de regeling permanent — zie de rode regels hieronder.
                  </span>
                </div>
              )}

              {uitgeklapt && (
                <div className="space-y-4 pt-2">
                  {/* --- Regels --- */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Toetsingsregels</h4>
                      <MetReden mag={rechten.regelToevoegen} reden={REDEN.geenSchrijfrecht}>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!mag(rechten.regelToevoegen)}
                          onClick={() => setRegelDialoog({ open: true, regelingId: regeling.id, regel: null })}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Regel
                        </Button>
                      </MetReden>
                    </div>
                    {eigenRegels.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Nog geen regels.</p>
                    ) : (
                      <div className="space-y-1">
                        {eigenRegels.map(regel => {
                          const bekend = vindIntakeVeld(regel.veld);
                          const operatorFout = bekend && !operatorPastBijVeld(regel.veld, regel.operator);
                          const uitleg = onbekendVeldUitleg(regel.veld, regel.verplicht);
                          return (
                            <div
                              key={regel.id}
                              className={`flex items-start justify-between gap-2 rounded border p-2 text-sm ${
                                uitleg || operatorFout ? 'border-red-300 bg-red-50' : ''
                              }`}
                            >
                              <div className="min-w-0 space-y-0.5">
                                <p className="font-medium">{regel.beschrijving}</p>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {bekend ? bekend.label : regel.veld}
                                  {' '}{OPERATOR_LABELS[regel.operator as RegelOperator] ?? regel.operator}
                                  {' '}{waardeAlsTekst(regel.waarde)}
                                  {regel.verplicht ? ' — verplicht' : ' — optioneel'}
                                </p>
                                {uitleg && <p className="text-xs text-red-700">{uitleg}</p>}
                                {operatorFout && (
                                  <p className="text-xs text-red-700">
                                    De vergelijking "{regel.operator}" past niet bij het type "{bekend?.type}" van dit
                                    veld; de engine leest deze regel altijd als niet-voldaan.
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <MetReden mag={rechten.regelBijwerken} reden={REDEN.geenSchrijfrecht}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Regel wijzigen"
                                    disabled={!mag(rechten.regelBijwerken)}
                                    onClick={() => setRegelDialoog({ open: true, regelingId: regeling.id, regel })}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </MetReden>
                                <MetReden mag={rechten.regelVerwijderen} reden={REDEN.geenSchrijfrecht}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Regel verwijderen"
                                    disabled={!mag(rechten.regelVerwijderen)}
                                    onClick={() => metVoorvertoning(
                                      { soort: 'regel-verwijderen', id: regel.id, regelingId: regeling.id },
                                      `Verwijderen: regel "${regel.beschrijving}". Een toets weghalen kan een regeling laten kwalificeren die nu op € 0 staat.`,
                                      null,
                                      arg => beheer.verwijderRegel.mutate(
                                        { id: regel.id, jaar, ...arg },
                                        { onSettled: () => setTeBevestigen(null) },
                                      ),
                                    )}
                                  >
                                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                  </Button>
                                </MetReden>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* --- Bedragen --- */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Bedragen</h4>
                      <MetReden mag={rechten.bedragToevoegen} reden={REDEN.geenSchrijfrecht}>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!mag(rechten.bedragToevoegen)}
                          onClick={() => setBedragDialoog({ open: true, regelingId: regeling.id, bedrag: null })}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Bedrag
                        </Button>
                      </MetReden>
                    </div>
                    {eigenBedragen.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Nog geen bedragen.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-muted-foreground">
                              <th className="p-1">Categorie</th>
                              <th className="p-1">Subcategorie</th>
                              <th className="p-1">Soort</th>
                              <th className="p-1 text-right">Bedrag</th>
                              <th className="p-1">Grootte</th>
                              <th className="p-1"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {eigenBedragen.map(b => (
                              <tr key={b.id} className="border-t">
                                <td className="p-1">{b.categorie}</td>
                                <td className="p-1 text-muted-foreground">{b.subcategorie ?? '-'}</td>
                                <td className="p-1">{b.bedrag_type}</td>
                                <td className="p-1 text-right tabular-nums">{b.bedrag}</td>
                                <td className="p-1 text-muted-foreground">{b.bedrijfsgrootte ?? 'alle'}</td>
                                <td className="p-1 text-right whitespace-nowrap">
                                  <MetReden mag={rechten.bedragBijwerken} reden={REDEN.geenSchrijfrecht}>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      title="Bedrag wijzigen"
                                      disabled={!mag(rechten.bedragBijwerken)}
                                      onClick={() => setBedragDialoog({ open: true, regelingId: regeling.id, bedrag: b })}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  </MetReden>
                                  <MetReden mag={rechten.bedragVerwijderen} reden={REDEN.geenSchrijfrecht}>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      title="Bedrag verwijderen"
                                      disabled={!mag(rechten.bedragVerwijderen)}
                                      onClick={() => metVoorvertoning(
                                        { soort: 'bedrag-verwijderen', id: b.id, regelingId: regeling.id },
                                        `Verwijderen: bedrag ${b.categorie}${b.subcategorie ? ` / ${b.subcategorie}` : ''}. Een tarief weghalen kan het bedrag verhogen — bij een aftrek wint dan de volgende schijf.`,
                                        null,
                                        arg => beheer.verwijderBedrag.mutate(
                                          { id: b.id, jaar, ...arg },
                                          { onSettled: () => setTeBevestigen(null) },
                                        ),
                                      )}
                                    >
                                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                    </Button>
                                  </MetReden>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <RegelingDialog
        open={regelingDialoog.open}
        onOpenChange={o => setRegelingDialoog(d => ({ ...d, open: o }))}
        regeling={regelingDialoog.regeling}
        jaar={jaar}
        magIdentiteit={rechten.regelingIdentiteit}
        magRekengrondslag={rechten.regelingRekengrondslag}
        isBezig={beheer.isBezig}
        onBewaar={invoer => metVoorvertoning(
          { soort: 'regeling', id: invoer.id ?? null, regelingId: invoer.id ?? '', rij: invoer.velden },
          invoer.id
            ? `Regeling ${invoer.velden.korte_naam} (${invoer.velden.code})`
            : `Nieuwe regeling ${invoer.velden.korte_naam} (${invoer.velden.code})`,
          leesUpdatedAt(regelingDialoog.regeling),
          arg => beheer.bewaarRegeling.mutate({ ...invoer, jaar, ...arg }, {
            onSuccess: () => setRegelingDialoog({ open: false, regeling: null }),
            // Ook bij een fout dicht: een dialoog die open blijft staan met een
            // werkende knop is precies hoe een tweede rij ontstaat.
            onSettled: () => setTeBevestigen(null),
          }),
        )}
      />

      <RegelDialog
        open={regelDialoog.open}
        onOpenChange={o => setRegelDialoog(d => ({ ...d, open: o }))}
        regelingId={regelDialoog.regelingId}
        regel={regelDialoog.regel}
        volgendeSortOrder={regels.filter(r => r.regeling_id === regelDialoog.regelingId).length + 1}
        isBezig={beheer.isBezig}
        onBewaar={invoer => metVoorvertoning(
          { soort: 'regel', id: invoer.id ?? null, regelingId: invoer.velden.regeling_id, rij: invoer.velden },
          `${invoer.id ? 'Regel wijzigen' : 'Nieuwe regel'}: "${invoer.velden.beschrijving}"`,
          leesUpdatedAt(regelDialoog.regel),
          arg => beheer.bewaarRegel.mutate({ ...invoer, jaar, ...arg }, {
            onSuccess: () => setRegelDialoog({ open: false, regelingId: '', regel: null }),
            // Ook bij een fout dicht: een dialoog die open blijft staan met een
            // werkende knop is precies hoe een tweede rij ontstaat.
            onSettled: () => setTeBevestigen(null),
          }),
        )}
      />

      <BedragDialog
        open={bedragDialoog.open}
        onOpenChange={o => setBedragDialoog(d => ({ ...d, open: o }))}
        regelingId={bedragDialoog.regelingId}
        bedrag={bedragDialoog.bedrag}
        volgendeSortOrder={bedragen.filter(b => b.regeling_id === bedragDialoog.regelingId).length + 1}
        isBezig={beheer.isBezig}
        onBewaar={invoer => metVoorvertoning(
          { soort: 'bedrag', id: invoer.id ?? null, regelingId: invoer.velden.regeling_id, rij: invoer.velden },
          `${invoer.id ? 'Bedrag wijzigen' : 'Nieuw bedrag'}: ${invoer.velden.categorie}`
            + `${invoer.velden.subcategorie ? ` / ${invoer.velden.subcategorie}` : ''}`,
          leesUpdatedAt(bedragDialoog.bedrag),
          arg => beheer.bewaarBedrag.mutate({ ...invoer, jaar, ...arg }, {
            onSuccess: () => setBedragDialoog({ open: false, regelingId: '', bedrag: null }),
            // Ook bij een fout dicht: een dialoog die open blijft staan met een
            // werkende knop is precies hoe een tweede rij ontstaat.
            onSettled: () => setTeBevestigen(null),
          }),
        )}
      />

      {/*
        De enige bevestiging in dit scherm. Hij vervangt ook de oude
        "Definitief verwijderen?"-vraag: die zei WAT je weghaalde maar niet wat
        dat met de bedragen doet, en juist bij een verwijdering is dat het punt
        — een aftrekschijf weghalen kan het bedrag verhógen.
      */}
      <VoorvertoningDialog
        voorvertoning={teBevestigen?.voorvertoning ?? null}
        titel={teBevestigen?.titel ?? ''}
        isBezig={beheer.isBezig}
        onAnnuleer={() => setTeBevestigen(null)}
        onBevestig={() => teBevestigen?.schrijf(teBevestigen.voorvertoning.bevestigingSleutel)}
      />
    </div>
  );
}
